import React from 'react';
import { GPS_ACCURACY_THRESHOLD_M, GPS_COLORS } from './gps';

export const STORE_MARKER_COLOR = '#6366f1';

export function getMarkerColor(distanceMeters) {
    if (distanceMeters == null) return GPS_COLORS.none;
    if (distanceMeters <= 100) return GPS_COLORS.precise;
    if (distanceMeters <= GPS_ACCURACY_THRESHOLD_M) return GPS_COLORS.moderate;
    return GPS_COLORS.deviated;
}

export function getDeviationLineColor(distanceMeters) {
    return getMarkerColor(distanceMeters);
}

export function getAuditPosition(visit) {
    const audit = visit.auditCoordinates;
    if (audit?.latitude != null && audit?.longitude != null) {
        return { lat: audit.latitude, lng: audit.longitude };
    }
    return null;
}

export function getStorePosition(visit) {
    const theoretical = visit.theoreticalCoordinates || visit.store?.coordinates;
    if (theoretical?.latitude != null && theoretical?.longitude != null) {
        return { lat: theoretical.latitude, lng: theoretical.longitude };
    }
    return null;
}

function getRoutePosition(visit) {
    return getAuditPosition(visit) || getStorePosition(visit);
}

export function formatCoord(position) {
    if (!position) return '—';
    return `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`;
}

export function createAuditMarkerIcon(number, bgColor, selected = false) {
    const size = selected ? 42 : 36;
    const r = selected ? 18 : 16;
    const fontSize = selected ? 16 : 14;
    const stroke = selected ? 4 : 3;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="${bgColor}" stroke="#fff" stroke-width="${stroke}"/>
        <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle" fill="#fff" font-size="${fontSize}" font-weight="700" font-family="Inter,system-ui,sans-serif">${number}</text>
    </svg>`;
    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: size, height: size },
        anchor: { x: size / 2, y: size / 2 }
    };
}

export function createStoreMarkerIcon(number, selected = false) {
    const size = selected ? 38 : 32;
    const stroke = selected ? 3 : 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <rect x="4" y="4" width="${size - 8}" height="${size - 8}" rx="6" fill="${STORE_MARKER_COLOR}" stroke="#fff" stroke-width="${stroke}"/>
        <text x="${size / 2}" y="${size / 2 + 4}" text-anchor="middle" fill="#fff" font-size="12" font-weight="700" font-family="Inter,system-ui,sans-serif">${number}</text>
    </svg>`;
    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: size, height: size },
        anchor: { x: size / 2, y: size / 2 }
    };
}

export function fitMapToPoints(map, points) {
    if (!map || !points.length || !window.google?.maps) return;
    if (points.length === 1) {
        map.setCenter(points[0]);
        map.setZoom(15);
        return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: 56 });
}

/** Map state for the route view: mappable visits, route path, deviation lines and focus handling. */
export function useRouteEfficiencyMap({ visits = [], onSelectVisit }) {
    const mapRef = React.useRef(null);

    const mappableVisits = React.useMemo(
        () =>
            visits
                .map((visit, index) => ({
                    visit,
                    index,
                    storePosition: getStorePosition(visit),
                    auditPosition: getAuditPosition(visit),
                    routePosition: getRoutePosition(visit)
                }))
                .filter((item) => item.storePosition != null || item.auditPosition != null),
        [visits]
    );

    const routePath = React.useMemo(() => mappableVisits.map((item) => item.routePosition).filter(Boolean), [mappableVisits]);

    const deviationLines = React.useMemo(() => {
        return visits
            .map((visit, index) => {
                const storePosition = getStorePosition(visit);
                const auditPosition = getAuditPosition(visit);
                if (!storePosition || !auditPosition) return null;
                return {
                    index,
                    distanceMeters: visit.distanceMeters,
                    path: [storePosition, auditPosition]
                };
            })
            .filter(Boolean);
    }, [visits]);

    const allPoints = React.useMemo(() => {
        const points = [];
        mappableVisits.forEach(({ storePosition, auditPosition, routePosition }) => {
            if (storePosition) points.push(storePosition);
            if (auditPosition) points.push(auditPosition);
            else if (routePosition) points.push(routePosition);
        });
        return points;
    }, [mappableVisits]);

    React.useEffect(() => {
        if (mapRef.current && allPoints.length) {
            fitMapToPoints(mapRef.current, allPoints);
        }
    }, [allPoints]);

    const focusVisit = (index) => {
        onSelectVisit?.(index);
        const visit = visits[index];
        if (!visit || !mapRef.current) return;

        const points = [getStorePosition(visit), getAuditPosition(visit)].filter(Boolean);
        if (points.length) {
            fitMapToPoints(mapRef.current, points);
        }
    };

    const onMapLoad = (map) => {
        mapRef.current = map;
        fitMapToPoints(map, allPoints);
    };

    return { mappableVisits, routePath, deviationLines, focusVisit, onMapLoad };
}
